import { openFolder } from "@/components/misc";
import { ChecklistItemCompletionProps, ChecklistItemProps, ProjectProps } from "@/interfaces/project_types";
import { useState } from "react";
import { ProjectEditComponent } from "./ProjectEditComponent";
import { GenericCheckbox } from "@/components/GenericForm";
import { toast } from "sonner";
import { updateChecklistItem } from "@/api/projects";
import { DateSelector } from "@/components/TableColumnSelector/SelectorBase";
import { SelectorInputTypeProps } from "@/components/TableColumnSelector/Interface";
import { AxiosResponse } from "axios";

/** This is for the Project Report Page. Not to be confused with the ProjectCard in the Dashboard */
export function ProjectCard({ project, index }: { project: ProjectProps; index: number; }) {
    const [current_text, setCurrentText] = useState(project.description || "");

    return (
        <div key={index} className="grid grid-cols-6 px-2 py-4 hover:bg-slate-100 transition border-b">
            <div className="col-start-2">
                <h5 className="text-blue-500 hover:underline cursor-pointer" onClick={() => openFolder(project.folder_location ?? "")}>
                    {project && project.project_id}
                </h5>
            </div>
            <div>
                {project && project.project_name}
            </div>
            <div>
                <ProjectEditComponent text={current_text} setCurrentText={setCurrentText} project_id={project.project_id || ""} />
            </div>
            <div className="col-span-2">
                <ChecklistHeader />
                <ChecklistItemContainer item={project.checklist_items } project_id={project.project_id ?? ""} />
            </div>
        </div>
    );
}

/**
 * This component displays the checklist part of each project.
 * This consists of the the actual checklist of items, with a checkbox to check off the item.
 * As well as the date of when it was completed.
 * 
 * @param item - The checklist item to display
 */
export function ChecklistItemContainer({ item, project_id }: { item: ChecklistItemCompletionProps, project_id: string }) {
    return (
        <div className="flex flex-col gap-2">
            <ChecklistItem item={item.SUBMITTAL_CHECKLIST} field_name="SUBMITTAL_CHECKLIST" label="SUBMITTAL" project_id={project_id} />
            <ChecklistItem item={item.DIG_TESS} field_name="DIG_TESS" label="DIGT ESS" project_id={project_id} />
            <ChecklistItem item={item.ISO} field_name="ISO" label="ISO" project_id={project_id} />
            <ChecklistItem item={item.THRID_PARTY_REVIEW} field_name="THRID_PARTY_REVIEW" label="3RD PARTY REVIEW" project_id={project_id} />
            <ChecklistItem item={item.FLOW_TEST} field_name="FLOW_TEST" label="FLOW TEST" project_id={project_id} />
            <ChecklistItem item={item.AG_HYDRO} field_name="AG_HYDRO" label="AG HYDRO" project_id={project_id} />
            <ChecklistItem item={item.UG_HYDRO} field_name="UG_HYDRO" label="UG HYDRO" project_id={project_id} />
            <ChecklistItem item={item.FDC_CAPS} field_name="FDC_CAPS" label="FDC CAPS" project_id={project_id} />
            <ChecklistItem item={item.FINAL} field_name="FINAL" label="FINAL" project_id={project_id} />
            <ChecklistItem item={item.CLOSEOUTS} field_name="CLOSEOUTS" label="CLOSEOUTS" project_id={project_id} />
        </div>
    )
}

/** This component is a small form that updates the completed status of the checklist item when the checkbox is clicked */
function ChecklistItem({ item, field_name, label, project_id }: { item: ChecklistItemProps, field_name: string, label: string, project_id: string }) {
    // Create Date object in local timezone to avoid off-by-one day bug
    const formatted_date = item.completed_date ? 
        new Date(item.completed_date + 'T00:00:00').toLocaleDateString() : ""
    const [is_completed, setIsCompleted] = useState(item.completed ?? false)
    const [completed_date, setCompletedDate] = useState<string>(formatted_date)

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked

        try {
            const response = await updateChecklistItem(project_id, field_name, checked)

            if (response.status === 200) {
                setIsCompleted(checked)

                if (checked) {
                    setCompletedDate(new Date().toLocaleDateString())
                } else {
                    setCompletedDate("")
                }

                console.log(field_name, checked)
                toast.success(`${label} updated to ${checked ? "Completed" : "Not Completed"}`)
            } else {
                toast.error("Error updating checklist item. Error 400")
            }
        } catch (error) {
            console.error("Error updating checklist item: ", error)
            toast.error("Error updating checklist item")
        }
    }

    const handleChangeDate = async (field_name: string, value: SelectorInputTypeProps): Promise<AxiosResponse> => {
        try {
            const response = await updateChecklistItem(project_id, field_name, true, value?.toString() ?? "")

            if (response.status === 200) {
                setCompletedDate(value?.toString() ?? "")
                toast.success("Completed date updated successfully")
                return response
            } else {
                toast.error("Error updating completed date")
                return response
            }
        } catch (error) {
            console.error("Error updating completed date: ", error)
            toast.error("Error updating completed date")
            throw error
        }
    }

    return (
        <div className="grid grid-cols-3 gap-2">

            <h5 className="font-bold">{label}</h5>

            <GenericCheckbox label={""} value={is_completed} name={field_name} onChange={handleChange} optional />
            {is_completed && <DateSelector field_name={field_name} value={completed_date} api_callback={(field_name, value) => handleChangeDate(field_name, value)} />}
        </div>
    )
}

function ChecklistHeader() {
    return (
        <div className="grid grid-cols-3 gap-2 border-b mb-2">
            <h5 className="font-bold">Name</h5>
            <h5 className="font-bold">Completed</h5>
            <h5 className="font-bold">Completed Date</h5>
        </div>
    )
}